// The frame every screen sits in: the grouped navigation on the left — the
// search at its top, the sending state and who you are at its foot — a
// translucent top bar that says where you are, the halt bar under it on every
// page while the platform is stopped, and the workspace.
//
// UI-8: the sidebar is attached to the left edge, full height, with a
// hairline border and a very light translucency (the mockup's floating glass
// panel, a notch less Mac).
//
// Below the md breakpoint the navigation folds into a drawer behind the menu
// button; the workspace keeps the whole width.
//
// A page that is a workspace — the template editor — asks for the whole of it
// with `usePageBleed` (layout.tsx): no padding, no reading width, and the
// column fixed to the window so the page's own panels do the scrolling.

import { useState } from "react";
import type { ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronRightIcon, MailIcon, MenuIcon } from "lucide-react";
import SendingHaltBar, { HaltStatusProvider, SendingState } from "@/components/SendingHaltBar";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { CrumbProvider, useCrumb } from "@/components/emc/shell/crumb";
import { BleedProvider, useBleed } from "@/components/emc/shell/layout";
import { placeOf, visibleNav } from "@/components/emc/shell/nav";
import { QuickSearchProvider, QuickSearchTrigger } from "@/components/emc/shell/QuickSearch";
import { UserMenu } from "@/components/emc/shell/UserMenu";

function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const isAdmin = useAuth().user?.role === "admin";
  return (
    <nav aria-label="Fő navigáció" className="flex flex-col gap-4 px-2.5 py-2">
      {visibleNav(isAdmin).map((group) => (
        <div key={group.label} className="flex flex-col gap-px">
          <p className="px-2.5 pt-1 pb-1.5 text-label font-semibold text-muted-foreground">{group.label}</p>
          {group.items.map((item) => {
            const Icon = item.icon;
            if (item.soon) {
              return (
                <div
                  key={item.to}
                  aria-disabled="true"
                  className="flex h-8 cursor-default items-center gap-2.5 rounded-control px-2.5 text-muted-foreground"
                >
                  <Icon className="size-4 shrink-0 opacity-60" />
                  <span className="flex-1 opacity-70">{item.label}</span>
                  <span className="text-label">hamarosan</span>
                </div>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end ?? false}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    "flex h-8 items-center gap-2.5 rounded-control px-2.5 text-sidebar-foreground transition-[background-color,box-shadow] hover:bg-sidebar-accent hover:no-underline",
                    isActive && "bg-card font-semibold shadow-card hover:bg-card dark:bg-fill dark:hover:bg-fill",
                  )
                }
              >
                <Icon className="size-4.5 shrink-0 text-sidebar-primary" strokeWidth={1.75} />
                <span className="truncate">{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

function Breadcrumb() {
  const { pathname } = useLocation();
  const crumb = useCrumb();
  const place = placeOf(pathname);
  if (place === null) return null;
  const onItsOwnPage = pathname === place.item.to;
  return (
    <nav aria-label="Hely" className="flex min-w-0 items-center gap-1.5 text-detail">
      <span className="hidden text-muted-foreground sm:inline">{place.group}</span>
      <ChevronRightIcon className="hidden size-3.5 shrink-0 text-tertiary sm:inline" />
      {onItsOwnPage && crumb === null ? (
        <span className="truncate font-medium">{place.item.label}</span>
      ) : (
        <>
          <NavLink to={place.item.to} end className="truncate text-muted-foreground hover:text-foreground">
            {place.item.label}
          </NavLink>
          {crumb !== null && (
            <>
              <ChevronRightIcon className="size-3.5 shrink-0 text-tertiary" />
              <span className="truncate font-medium">{crumb}</span>
            </>
          )}
        </>
      )}
    </nav>
  );
}

/** The whole sidebar: the app's name, the search, the map, the footer. */
function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2.5 px-4 pt-4 pb-3">
        <span
          aria-hidden="true"
          className="flex size-8 shrink-0 items-center justify-center rounded-control bg-app-icon text-primary-foreground shadow-button"
        >
          <MailIcon className="size-4.5" strokeWidth={2} />
        </span>
        <span className="truncate text-body font-semibold">Email marketing</span>
      </div>
      <div className="px-3 pb-2">
        <QuickSearchTrigger />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <SidebarNav onNavigate={onNavigate} />
      </div>
      <div className="flex flex-col gap-1.5 border-t border-separator p-2.5">
        <SendingState />
        <UserMenu />
      </div>
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  return (
    <CrumbProvider>
      <BleedProvider>
        <HaltStatusProvider>
          <QuickSearchProvider>
            <Frame>{children}</Frame>
          </QuickSearchProvider>
        </HaltStatusProvider>
      </BleedProvider>
    </CrumbProvider>
  );
}

function Frame({ children }: { children: ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const bleed = useBleed();

  // A workspace folds the navigation behind the menu button at every width,
  // the way a phone does: a letter is 600px wide, and a laptop's screen has
  // no room for the navigation, two panels and the letter at once.
  return (
    <div className={cn("min-h-screen", !bleed && "md:grid md:grid-cols-[var(--emc-sidebar-width)_minmax(0,1fr)]")}>
      <aside className={cn("glass-sidebar z-20 hidden border-r border-sidebar-border", !bleed && "md:block")}>
        <div className="sticky top-0 h-screen">
          <Sidebar />
        </div>
      </aside>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className={cn("gap-0 bg-sidebar", !bleed && "md:hidden")}>
          <SheetTitle className="sr-only">Navigáció</SheetTitle>
          <SheetDescription className="sr-only">Az admin oldalai, csoportonként</SheetDescription>
          <Sidebar onNavigate={() => setMenuOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className={cn("flex min-w-0 flex-col", bleed && "h-screen")}>
        {/* Translucent over the page as it scrolls beneath; the halt bar
            under it keeps its own red tint on the same glass. */}
        <div className="glass sticky top-0 z-30">
          <header className="flex h-13 items-center gap-3 border-b border-separator px-4 md:px-8">
            <Button
              variant="ghost"
              size="icon-sm"
              className={cn(!bleed && "md:hidden")}
              aria-label="Navigáció megnyitása"
              onClick={() => setMenuOpen(true)}
            >
              <MenuIcon />
            </Button>
            <div className="min-w-0 flex-1">
              <Breadcrumb />
            </div>
            <QuickSearchTrigger variant="icon" className={cn(!bleed && "md:hidden")} />
          </header>
          {/* F1: on every screen, because a stopped platform is not a fact
              about one page. */}
          <SendingHaltBar />
        </div>
        <main
          className={cn(
            "min-w-0",
            bleed
              ? "flex min-h-0 w-full flex-1 flex-col"
              : "mx-auto w-full max-w-content px-4 pt-6 pb-14 md:px-8 md:pt-7",
          )}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
